import { createSlice } from '@reduxjs/toolkit';
import { RootState } from '../..';
import {
    AutoReplyResponse,
    updateAutoReply,
    fetchAutoReplies,
} from './autoRepliesAPI';
import IConnectToTeam from '../../../model/IConnectToTeam';
import { IPagination, LoadStatus } from '../../../model';
import { AutoReplyMessageType } from '../../../model/AutoReplyMessageType';
import { createAppThunk } from '../../thunks';

export interface AutoRepliesPanelState {
    items: IConnectToTeam[];
    nextPageToken: string | null;
    search: string | null;
    loadStatus: LoadStatus;
    pagination: IPagination;
}

const initialState: AutoRepliesPanelState = {
    loadStatus: 'loading',
    items: [],
    nextPageToken: null,
    search: null,
    pagination: {
        currentPage: 1,
        pageSize: 10,
        totalCount: 0,
    },
};

export const loadAutoReplies = createAppThunk<AutoReplyResponse, void>(
    'autoRepliesPanel/loadAutoReplies',
    async (_, { getState }) => {
        const {
            autoRepliesPanel: { nextPageToken, search },
        } = getState();

        const queryParams = new Map();

        if (!!search) {
            queryParams.set('search', search);
        }

        if (!!nextPageToken) {
            queryParams.set('nextPageToken', nextPageToken);
        }

        const query: string[] = [];
        queryParams.forEach((val, key) => {
            if (query.length) {
                query.push(`&${key}=${val}`);
            } else {
                query.push(`?${key}=${val}`);
            }
        });

        return await fetchAutoReplies(query.join(''));
    }
);

export const updateAutoReplies = createAppThunk<
    IConnectToTeam,
    {
        rowKey: string;
        autoReply?: string;
        closedConnectionReply?: string;
        messageType: AutoReplyMessageType;
    }
>('autoRepliesPanel/updateAutoReply', async (data) => {
    const result = await updateAutoReply(data);
    return result;
});

export const searchAutoReplies = createAppThunk<AutoReplyResponse, string>(
    'autoRepliesPanel/searchAutoReplies',
    async (search) => {
        const query: string = `?search=${search}`;
        return await fetchAutoReplies(query);
    }
);

export const autoRepliesSlice = createSlice({
    name: 'autoRepliesPanel',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(loadAutoReplies.pending, (state) => {
                state.loadStatus = 'loading';
            })
            .addCase(loadAutoReplies.fulfilled, (state, action) => {
                state.loadStatus = 'idle';
                state.items = action.payload.items ?? [];

                state.nextPageToken = !!action.payload.nextPageToken
                    ? JSON.stringify(action.payload.nextPageToken)
                    : null;
                state.search = action.payload.search;
            })
            .addCase(loadAutoReplies.rejected, (state) => {
                state.loadStatus = 'failed';
            })
            .addCase(updateAutoReplies.pending, (state) => {
                state.loadStatus = 'loading';
            })
            .addCase(updateAutoReplies.fulfilled, (state, action) => {
                state.loadStatus = 'idle';
                state.items = state.items.map((x) => {
                    if (x.key === action.payload.key) {
                        return {
                            ...x,
                            ...action.payload,
                        };
                    }
                    return x;
                });
            })
            .addCase(updateAutoReplies.rejected, (state) => {
                state.loadStatus = 'failed';
            })
            .addCase(searchAutoReplies.pending, (state) => {
                state.loadStatus = 'loading';
            })
            .addCase(searchAutoReplies.fulfilled, (state, action) => {
                state.loadStatus = 'idle';
                state.items = action.payload.items ?? [];
                state.nextPageToken = !!action.payload.nextPageToken
                    ? JSON.stringify(action.payload.nextPageToken)
                    : null;
                state.search = action.payload.search;
            })
            .addCase(searchAutoReplies.rejected, (state) => {
                state.loadStatus = 'failed';
            });
    },
});

export const selectAutoReplies = (state: RootState) => state.autoRepliesPanel;

export default autoRepliesSlice.reducer;
